/**
 * Color utility functions for background removal
 */

/**
 * Calculate Euclidean distance between two RGB colors
 * Normalized to 0-1 range (max distance is ~441.67 for RGB)
 * @param {number} r1 - Red value of first color (0-255)
 * @param {number} g1 - Green value of first color (0-255)
 * @param {number} b1 - Blue value of first color (0-255)
 * @param {number} r2 - Red value of second color (0-255)
 * @param {number} g2 - Green value of second color (0-255)
 * @param {number} b2 - Blue value of second color (0-255)
 * @returns {number} Distance between 0 and 1
 */
export function colorDistance(r1, g1, b1, r2, g2, b2) {
  return Math.sqrt(
    Math.pow(r1 - r2, 2) +
    Math.pow(g1 - g2, 2) +
    Math.pow(b1 - b2, 2)
  ) / 441.67; // sqrt(255^2 + 255^2 + 255^2) ≈ 441.67
}

/**
 * Sample background color from image corners
 * Takes average of corner pixels to handle gradients
 * @param {ImageData} imageData - Canvas ImageData object
 * @returns {Object} RGB color object { r, g, b }
 */
export function sampleBackgroundColor(imageData) {
  const { width, height, data } = imageData;

  // Sample from four corners and center of edges (8 points total)
  const samplePoints = [
    { x: 0, y: 0 },                           // Top-left
    { x: width - 1, y: 0 },                   // Top-right
    { x: 0, y: height - 1 },                  // Bottom-left
    { x: width - 1, y: height - 1 },          // Bottom-right
    { x: Math.floor(width / 2), y: 0 },       // Top-center
    { x: Math.floor(width / 2), y: height - 1 }, // Bottom-center
    { x: 0, y: Math.floor(height / 2) },      // Left-center
    { x: width - 1, y: Math.floor(height / 2) } // Right-center
  ];

  let totalR = 0, totalG = 0, totalB = 0;

  for (const point of samplePoints) {
    const index = (point.y * width + point.x) * 4;
    totalR += data[index];
    totalG += data[index + 1];
    totalB += data[index + 2];
  }

  const count = samplePoints.length;
  return {
    r: Math.round(totalR / count),
    g: Math.round(totalG / count),
    b: Math.round(totalB / count)
  };
}

/**
 * Remove background from image data based on sampled color and tolerance
 * @param {ImageData} imageData - Canvas ImageData object
 * @param {number} tolerance - Tolerance threshold (0-1, default 0.15)
 * @param {Object} bgColor - Optional background color override { r, g, b }
 * @returns {ImageData} Modified ImageData with transparent background
 */
export function removeBackground(imageData, tolerance = 0.15, bgColor = null) {
  const { data } = imageData;

  // Sample background color if not provided
  const backgroundColor = bgColor || sampleBackgroundColor(imageData);

  // Process each pixel
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];

    // Calculate color distance
    const distance = colorDistance(
      r, g, b,
      backgroundColor.r, backgroundColor.g, backgroundColor.b
    );

    // If within tolerance, make transparent
    if (distance < tolerance) {
      data[i + 3] = 0; // Set alpha to 0 (fully transparent)
    }
  }

  return imageData;
}

/**
 * Apply edge softening to reduce artifacts around removed background
 * @param {ImageData} imageData - Canvas ImageData object with transparency
 * @param {number} radius - Softening radius in pixels (default 1)
 * @returns {ImageData} Modified ImageData with softened edges
 */
export function softenEdges(imageData, radius = 1) {
  const { width, height, data } = imageData;
  const newData = new Uint8ClampedArray(data);

  // Simple box blur on alpha channel
  for (let y = radius; y < height - radius; y++) {
    for (let x = radius; x < width - radius; x++) {
      const index = (y * width + x) * 4;

      // Skip fully transparent or fully opaque pixels
      const alpha = data[index + 3];
      if (alpha === 0 || alpha === 255) continue;

      // Average alpha values in neighborhood
      let totalAlpha = 0;
      let count = 0;

      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          const neighborIndex = ((y + dy) * width + (x + dx)) * 4;
          totalAlpha += data[neighborIndex + 3];
          count++;
        }
      }

      newData[index + 3] = Math.round(totalAlpha / count);
    }
  }

  // Copy back to original data
  for (let i = 0; i < data.length; i++) {
    data[i] = newData[i];
  }

  return imageData;
}

/**
 * Detect if image has a gradient background
 * @param {ImageData} imageData - Canvas ImageData object
 * @returns {boolean} True if gradient detected
 */
export function hasGradientBackground(imageData) {
  const { width, height, data } = imageData;

  // Sample corners
  const corners = [
    { x: 0, y: 0 },
    { x: width - 1, y: 0 },
    { x: 0, y: height - 1 },
    { x: width - 1, y: height - 1 }
  ];

  const colors = corners.map(point => {
    const index = (point.y * width + point.x) * 4;
    return { r: data[index], g: data[index + 1], b: data[index + 2] };
  });

  // Check variance between corners
  let maxDistance = 0;
  for (let i = 0; i < colors.length; i++) {
    for (let j = i + 1; j < colors.length; j++) {
      const distance = colorDistance(
        colors[i].r, colors[i].g, colors[i].b,
        colors[j].r, colors[j].g, colors[j].b
      );
      maxDistance = Math.max(maxDistance, distance);
    }
  }

  // If max distance > 0.1 (10%), consider it a gradient
  return maxDistance > 0.1;
}
